import { useAuthentication } from '@src/features/auth';
import { useMemo } from 'react';

import {
  editableSegments,
  NO_CLIENT_ACCESS,
  viewableSegments,
} from '../../lib/access';
import type { BusinessSegment } from '../../types';

/**
 * What the signed-in user may do in the Clients module, per business
 * segment. Pages use it for their read-only mode; the backend checks the
 * same rights on every request.
 */
export function useClientAccess() {
  const { user } = useAuthentication();
  const access = user?.clientAccess ?? NO_CLIENT_ACCESS;

  return useMemo(() => {
    const viewable = viewableSegments(access);
    const editable = editableSegments(access);
    return {
      access,
      viewableSegments: viewable,
      editableSegments: editable,
      /** May open the module: viewing right for at least one segment. */
      canView: viewable.length > 0,
      /** Editing right for at least one segment. */
      canEdit: editable.length > 0,
      canViewSegment: (segment: BusinessSegment) => access[segment].view,
      canEditSegment: (segment: BusinessSegment) => access[segment].edit,
      /** Sees both segments: the few users who get segment toggles. */
      seesBothSegments: viewable.length > 1,
    };
  }, [access]);
}
